/**
 * Feature Flags - Single source of truth
 *
 * - All flags defined in one place with types and documentation
 * - Defaults from env (REACT_APP_FEATURE_*) for build-time config
 * - Optional localStorage override for runtime toggling (e.g. /feature-flags page)
 *
 * Onboarding wizard, create-org flow, and cleaning standards step are always part of the
 * app flow and do not use feature flags. To add a new flag in the future: add the key here,
 * add its default in getDefaultFlags(), and add label/description on the Feature Flags page.
 *
 * Env vars (for any future flags):
 *   REACT_APP_FEATURE_*=true|false
 *   REACT_APP_ENABLE_FEATURE_FLAGS_UI=true  → show /feature-flags page
 */

/**
 * Flag keys - use these everywhere instead of magic strings.
 * Currently empty; add new keys here when needed.
 */
export const FEATURE_FLAGS = {};

/**
 * localStorage key for runtime overrides (staging only)
 * Overrides are merged on top of env defaults
 */
export const FEATURE_FLAGS_STORAGE_KEY = 'janway_feature_flags';

/**
 * Default values for all flags (env-based). Used by /feature-flags page and any future flag-gated features.
 * To add a new flag: add to FEATURE_FLAGS and return its default here (e.g. from process.env).
 */
export const getDefaultFlags = () => ({});

/**
 * Whether the Feature Flags admin UI is allowed.
 * Pre-launch: true in all environments (staging + production) so the page is visible everywhere.
 * After launch: set REACT_APP_ENABLE_FEATURE_FLAGS_UI=true only in Vercel Preview so the page shows only in staging.
 */
export const isFeatureFlagsUIEnabled = () =>
  process.env.NODE_ENV === 'development' ||
  process.env.REACT_APP_ENABLE_FEATURE_FLAGS_UI === 'true' ||
  true; // pre-launch: show in staging and production; remove "|| true" when launching to hide in production

/**
 * Whether the user has saved overrides from the Feature Flags page (localStorage).
 * When true, they explicitly toggled at least one flag on /feature-flags → use full login flow.
 * When false (and UI is enabled), staging goes direct to dashboard.
 */
export const hasFeatureFlagOverrides = () => {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null && Object.keys(parsed).length > 0;
  } catch {
    return false;
  }
};

/**
 * URL to fetch app-wide feature flags at runtime (no build needed to change).
 * - If set (e.g. REACT_APP_FEATURE_FLAGS_URL): fetch from that URL (Gist, S3, backend, etc.).
 * - If not set: fetch from same-origin /feature-flags.json (archivo en public/).
 *
 * Para cambiar flags para toda la app sin hacer build:
 * 1. Editar public/feature-flags.json (o el JSON en la URL configurada).
 * 2. Subir solo ese archivo al servidor si usas /feature-flags.json.
 * 3. En la próxima carga (o refresh) todos los usuarios reciben los nuevos flags.
 */
export const getFeatureFlagsUrl = () =>
  typeof window !== 'undefined'
    ? process.env.REACT_APP_FEATURE_FLAGS_URL || `${window.location.origin}/feature-flags.json`
    : null;
